import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { apiClient } from "@/lib/api/client";
import type { AddressBookEntry, CreateAddressPayload } from "@/lib/api/types";
import {
  CheckCircle2,
  Download,
  Edit2,
  Plus,
  Search,
  ShieldCheck,
  Star,
  StarOff,
  Trash2,
  X,
  XCircle,
} from "lucide-react";
import * as React from "react";

type DialogMode = "create" | "edit";

type AddressFormState = {
  name: string;
  company: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone: string;
  email: string;
  isFavorite: boolean;
};

function emptyForm(): AddressFormState {
  return {
    name: "",
    company: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
    phone: "",
    email: "",
    isFavorite: false,
  };
}

function entryToForm(e: AddressBookEntry): AddressFormState {
  return {
    name: e.name,
    company: e.company,
    address: e.address,
    address2: e.address2,
    city: e.city,
    state: e.state,
    zip: e.zip,
    country: e.country,
    phone: e.phone,
    email: e.email,
    isFavorite: e.isFavorite,
  };
}

function formToPayload(form: AddressFormState): CreateAddressPayload {
  return {
    name: form.name,
    company: form.company,
    address: form.address,
    address2: form.address2,
    city: form.city,
    state: form.state,
    zip: form.zip,
    country: form.country,
    phone: form.phone,
    email: form.email,
    isFavorite: form.isFavorite,
  };
}

export default function AddressBookPage() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const [addresses, setAddresses] = React.useState<AddressBookEntry[]>([]);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [addressLimit, setAddressLimit] = React.useState<number | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState<DialogMode>("create");
  const [dialogSaving, setDialogSaving] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<AddressFormState>(emptyForm());

  // Validation state (per-address)
  const [validatingId, setValidatingId] = React.useState<string | null>(null);

  const load = React.useCallback(async (search?: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.getAddresses({ search: search || undefined });
      setAddresses(data.addresses);
      setAddressLimit(data.limit);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to load addresses.");
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  // Debounced search
  const searchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearch = React.useCallback(
    (value: string) => {
      setSearchQuery(value);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        void load(value);
      }, 300);
    },
    [load],
  );

  // Keyboard handler for dialog
  React.useEffect(() => {
    if (!dialogOpen) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDialogOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [dialogOpen]);

  const flashMessage = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(null), 3000);
  };

  const openCreate = () => {
    setDialogMode("create");
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (entry: AddressBookEntry) => {
    setDialogMode("edit");
    setEditingId(entry.id);
    setForm(entryToForm(entry));
    setDialogOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setDialogSaving(true);
    setError(null);
    try {
      const payload = formToPayload(form);
      if (dialogMode === "edit" && editingId) {
        const updated = await apiClient.updateAddress(editingId, payload);
        setAddresses((prev) => prev.map((a) => (a.id === editingId ? updated : a)));
        flashMessage("Address updated.");
      } else {
        const created = await apiClient.createAddress(payload);
        setAddresses((prev) => [...prev, created]);
        flashMessage("Address saved.");
      }
      setDialogOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save address.");
    } finally {
      setDialogSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setError(null);
    try {
      await apiClient.deleteAddress(id);
      setAddresses((prev) => prev.filter((a) => a.id !== id));
      flashMessage("Address deleted.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to delete address.");
    }
  };

  const handleToggleFavorite = async (id: string) => {
    setError(null);
    try {
      const updated = await apiClient.toggleAddressFavorite(id);
      setAddresses((prev) =>
        prev
          .map((a) => (a.id === id ? updated : a))
          .sort((a, b) => {
            if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
            return a.name.localeCompare(b.name);
          }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to toggle favorite.");
    }
  };

  const handleValidate = async (id: string) => {
    setError(null);
    setValidatingId(id);
    try {
      const result = await apiClient.validateAddress(id);
      setAddresses((prev) => prev.map((a) => (a.id === id ? result.address : a)));
      if (result.validation.valid) {
        flashMessage("Address validated and standardized.");
      } else {
        setError(result.validation.error ?? "Address could not be validated.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to validate address.");
    } finally {
      setValidatingId(null);
    }
  };

  const handleExportCsv = async () => {
    setError(null);
    try {
      const blob = await apiClient.exportAddressesCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `address-book-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      flashMessage("Addresses exported.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to export addresses.");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="gap-3 md:flex md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle>Address Book</CardTitle>
            <CardDescription>
              Manage saved addresses for quick quoting and booking.
              {addressLimit !== null ? ` (${addresses.length}/${addressLimit} used)` : null}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              disabled={addresses.length === 0}
            >
              <Download className="size-4" />
              Export CSV
            </Button>
            <Button type="button" size="sm" onClick={openCreate}>
              <Plus className="size-4" />
              Add Address
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search bar */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search addresses by name, company, city, ZIP..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}
          {message ? <p className="mb-4 text-sm text-emerald-600">{message}</p> : null}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="md" />
            </div>
          ) : addresses.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center text-muted-foreground">
              <p>{searchQuery ? "No addresses match your search." : "No addresses yet."}</p>
              {!searchQuery ? (
                <Button type="button" size="sm" onClick={openCreate}>
                  <Plus className="size-4" />
                  Add Your First Address
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {addresses.map((entry) => (
                <AddressCard
                  key={entry.id}
                  entry={entry}
                  onEdit={() => openEdit(entry)}
                  onDelete={() => handleDelete(entry.id)}
                  onToggleFavorite={() => handleToggleFavorite(entry.id)}
                  onValidate={() => handleValidate(entry.id)}
                  validating={validatingId === entry.id}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      {dialogOpen ? (
        <AddressDialog
          mode={dialogMode}
          form={form}
          setForm={setForm}
          saving={dialogSaving}
          onSubmit={handleSave}
          onClose={() => setDialogOpen(false)}
          error={error}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Address Card
// ---------------------------------------------------------------------------

function AddressCard({
  entry,
  onEdit,
  onDelete,
  onToggleFavorite,
  onValidate,
  validating,
}: {
  entry: AddressBookEntry;
  onEdit: () => void;
  onDelete: () => void;
  onToggleFavorite: () => void;
  onValidate: () => void;
  validating: boolean;
}) {
  return (
    <div className="group relative rounded-lg border-[0.5px] border-border bg-card p-4 transition-colors hover:border-foreground/20">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-semibold">
              {entry.name || entry.company || "Unnamed Address"}
            </h3>
            {entry.isValidated ? (
              <Badge variant="outline" className="shrink-0 gap-1 border-emerald-200 bg-emerald-50 text-[0.65rem] text-emerald-700">
                <CheckCircle2 className="size-3" />
                Validated
              </Badge>
            ) : (
              <Badge variant="outline" className="shrink-0 gap-1 text-[0.65rem] text-muted-foreground">
                <XCircle className="size-3" />
                Unvalidated
              </Badge>
            )}
          </div>
          {entry.company && entry.name ? (
            <p className="truncate text-xs text-muted-foreground">{entry.company}</p>
          ) : null}
        </div>
        <button
          type="button"
          className="shrink-0 text-muted-foreground transition-colors hover:text-yellow-500"
          onClick={onToggleFavorite}
          aria-label={entry.isFavorite ? "Remove from favorites" : "Add to favorites"}
        >
          {entry.isFavorite ? (
            <Star className="size-4 fill-yellow-400 text-yellow-500" />
          ) : (
            <StarOff className="size-4" />
          )}
        </button>
      </div>

      <div className="space-y-0.5 text-xs text-muted-foreground">
        <p>{entry.address}</p>
        {entry.address2 ? <p>{entry.address2}</p> : null}
        <p>
          {entry.city}, {entry.state} {entry.zip}
        </p>
        {entry.phone ? <p>{entry.phone}</p> : null}
        {entry.email ? <p>{entry.email}</p> : null}
      </div>

      <div className="mt-3 flex items-center gap-1.5">
        {!entry.isValidated ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={onValidate}
            disabled={validating}
          >
            {validating ? (
              <>
                <Spinner size="sm" />
                Validating...
              </>
            ) : (
              <>
                <ShieldCheck className="size-3.5" />
                Validate
              </>
            )}
          </Button>
        ) : (
          <div className="flex-1" />
        )}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onEdit}
          aria-label="Edit address"
        >
          <Edit2 className="size-3.5" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="text-destructive hover:text-destructive"
          onClick={onDelete}
          aria-label="Delete address"
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Address Dialog (create / edit overlay)
// ---------------------------------------------------------------------------

function AddressDialog({
  mode,
  form,
  setForm,
  saving,
  onSubmit,
  onClose,
  error,
}: {
  mode: DialogMode;
  form: AddressFormState;
  setForm: React.Dispatch<React.SetStateAction<AddressFormState>>;
  saving: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  error: string | null;
}) {
  const update = <K extends keyof AddressFormState>(
    key: K,
    value: AddressFormState[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={mode === "edit" ? "Edit Address" : "Add Address"}
    >
      <div className="relative mx-4 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border-[0.5px] border-border bg-card">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-card px-6 py-4">
          <h2 className="text-lg font-semibold">
            {mode === "edit" ? "Edit Address" : "Add Address"}
          </h2>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="size-5" />
          </button>
        </div>

        <form className="space-y-4 px-6 py-4" onSubmit={onSubmit}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Contact Name</Label>
              <Input
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Company</Label>
              <Input
                value={form.company}
                onChange={(e) => update("company", e.target.value)}
                placeholder="Acme Inc."
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Address</Label>
            <Input
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
              placeholder="123 Main St"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label>Address Line 2</Label>
            <Input
              value={form.address2}
              onChange={(e) => update("address2", e.target.value)}
              placeholder="Suite 100"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>City</Label>
              <Input
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>State</Label>
              <Input
                value={form.state}
                onChange={(e) => update("state", e.target.value)}
                maxLength={10}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>ZIP</Label>
              <Input
                value={form.zip}
                onChange={(e) => update("zip", e.target.value)}
                maxLength={20}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="(555) 123-4567"
                type="tel"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="contact@company.com"
                type="email"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isFavorite}
                onChange={(e) => update("isFavorite", e.target.checked)}
                className="size-4 rounded border-border"
              />
              Favorite (shown at top of list)
            </Label>
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" className="text-primary-foreground" />
                  Saving...
                </>
              ) : mode === "edit" ? (
                "Update Address"
              ) : (
                "Save Address"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
